import { computed, ref, watch, toRef, unref, isRef, onBeforeUnmount, type Ref } from "vue";
import type { TSelectFieldProps } from "@/types/selectField";
import type { TItemProps } from "@/types/input";
import { debounce } from "@/utils/Debounce";
import { useField } from "vee-validate";

export function useSelectOptionItems(
  props: TSelectFieldProps,
  value: Ref,
  onSearchChange?: (val: string) => void
) {
  const canAutoSelectSingleItem = computed(() => props.autoSelectSingleItem !== false);
  const { value: existingDataValue } = useField(props.exisitingDataField ?? "");
  const cachedSelectedItems = ref<any[]>([]);
  const isSearching = ref(false);
  const hasInteracted = ref(false);
  const singleItemCheckStatus = ref<"idle" | "checking" | "success" | "failed">("idle");
  const originalPageSize = ref<number>(20);

  const queryFiltersRef = toRef(props.queryFilters);

  const isDynamic = computed(() => !!props.paginatedQuery || !!queryFiltersRef.value);
  const effectiveNoFilters = computed(() => {
    if (props.noFilters !== undefined) return props.noFilters;
    return isDynamic.value;
  });

  const itemProps = computed(() => {
    const baseMapper = props.itemProps
      ? props.itemProps
      : (val: any): TItemProps => ({
          title: val.title || val.name || val.label || String(val),
          value: val.value || val.id || val,
          // Carried through so plain `items` can supply a subtitle without the
          // caller having to hand-roll an `item-props` mapper. Guarded because
          // `val` may be a primitive (see the `String(val)` title fallback).
          subtitle: val === null || typeof val !== "object" ? undefined : val.subtitle,
        });

    return (val: any) => {
      const item = baseMapper(val);
      let disabled = item?.disabled || val.disabled || false;
      if (props.itemDisabled) {
        if (typeof props.itemDisabled === "function") {
          disabled = props.itemDisabled(val);
        } else {
          disabled = val?.[props.itemDisabled];
        }
      }
      return { ...item, disabled } as TItemProps;
    };
  });

  function itemExistsInList(items: unknown[], candidate: unknown): boolean {
    const candidateP = itemProps.value(candidate);
    return items.some((it) => {
      const itP = itemProps.value(it);
      return (
        (itP?.value && candidateP?.value && itP.value === candidateP.value) || it === candidate
      );
    });
  }

  function prependIfMissing(items: unknown[], candidate: unknown) {
    if (!itemExistsInList(items, candidate)) {
      items.unshift(candidate);
    }
  }

  function appendIfMissing(items: unknown[], candidate: unknown) {
    if (!itemExistsInList(items, candidate)) {
      items.push(candidate);
    }
  }

  const fetchedItem = computed(() => unref(props.itemQuery?.data) ?? null);

  // When a value is set from outside (PMS import, edit load) and we fetch the missing
  // item by id, write it into the existing-data field so the form holds full detail.
  watch(fetchedItem, (item) => {
    if (!props.exisitingDataField || !item) return;
    if (existingDataValue.value !== item) {
      existingDataValue.value = item;
    }
  });

  const optionItems = computed(() => {
    let items: any[] = [];

    const paginatedData = unref(props.paginatedQuery?.data);
    const queryAllData = unref(props.queryAll?.data);

    if (props.paginatedQuery && paginatedData) {
      // Result Masking:
      // If we failed the single item check AND haven't interacted yet,
      // we should not show the partial result (which is likely just 1 item from the check).
      if (singleItemCheckStatus.value === "failed" && !hasInteracted.value) {
        items = [];
      } else {
        items = paginatedData.pages.flatMap((p: any) => p.results) ?? [];
      }
    } else if (props.queryAll && queryAllData) {
      items = [...(queryAllData ?? [])];
    } else {
      items = [...(props.items ?? [])];
    }

    if (props.appendItems) {
      const toAppend = Array.isArray(props.appendItems) ? props.appendItems : [props.appendItems];
      toAppend.forEach((item: unknown) => prependIfMissing(items, item));
    }

    if (props.filterItemsCallback) {
      items = items.filter(props.filterItemsCallback);
    }

    // Prefer a freshly fetched item over a stub on the existing-data field so the
    // label/short_name from the detail endpoint wins once the query resolves.
    if (fetchedItem.value) {
      appendIfMissing(items, fetchedItem.value);
    }

    if (props.exisitingDataField && existingDataValue.value) {
      const existingP = itemProps.value(existingDataValue.value);
      if (existingP?.value != null) {
        appendIfMissing(items, existingDataValue.value);
      }
    }

    if (cachedSelectedItems.value.length) {
      cachedSelectedItems.value.forEach((cached) => appendIfMissing(items, cached));
    }

    return items;
  });

  // --- Auto-selection ---

  function selectItem(item: any) {
    const itemVal = itemProps.value(item)?.value;
    if (itemVal === undefined) return;

    // Multi-select fields always store an array; writing a bare value breaks yup
    // `array()` schemas and chip rendering (object shows up, validation fails).
    if (props.multiple) {
      const selected = props.returnObject ? item : itemVal;
      if (value.value == null || (Array.isArray(value.value) && value.value.length === 0)) {
        value.value = [selected];
      }
      return;
    }

    if (props.returnObject) {
      if (value.value !== item) value.value = item;
    } else {
      if (value.value !== itemVal) value.value = itemVal;
    }
  }

  function restorePageSize(shouldRefetch: boolean) {
    const filters = unref(queryFiltersRef);
    if (filters && originalPageSize.value) {
      filters.page_size = originalPageSize.value;
      if (shouldRefetch) props.paginatedQuery?.refetch();
    }
  }

  // Handles both single-item check resolution and auto-select when only 1 item exists
  watch(
    [
      optionItems,
      () => props.paginatedQuery?.isFetching?.value,
      () => props.queryAll?.isFetching?.value,
    ],
    ([items, isPaginatedFetching, isQueryAllFetching]) => {
      if (singleItemCheckStatus.value === "checking") {
        if (props.paginatedQuery) {
          if (props.paginatedQuery.isFetching.value) return;

          if (items.length === 1 && !props.paginatedQuery.hasNextPage.value) {
            singleItemCheckStatus.value = "success";
            if (canAutoSelectSingleItem.value) {
              selectItem(items[0]);
            }
            restorePageSize(false);
          } else {
            singleItemCheckStatus.value = "failed";
            restorePageSize(false);
          }
        } else if (props.queryAll) {
          if (props.queryAll.isFetching.value) return;

          if (items.length === 1) {
            singleItemCheckStatus.value = "success";
            if (canAutoSelectSingleItem.value) {
              selectItem(items[0]);
            }
          } else {
            singleItemCheckStatus.value = "failed";
          }
        }
        return;
      }

      if (
        !isSearching.value &&
        (!existingDataValue.value || (existingDataValue.value as any)[props.fieldName] == null) &&
        value.value == null &&
        !props.disabled
      ) {
        if (props.paginatedQuery) {
          if (isPaginatedFetching) return;
          if (items.length === 1 && !props.paginatedQuery.hasNextPage.value) {
            if (canAutoSelectSingleItem.value) {
              selectItem(items[0]);
            }
          }
        } else {
          if (isQueryAllFetching) return;
          if (items.length === 1 && canAutoSelectSingleItem.value) {
            selectItem(items[0]);
          }
        }
      }
    },
    { immediate: true, deep: true }
  );

  // Caches selected items across paginated page changes and fetches missing items by ID
  watch(
    () => (Array.isArray(value.value) ? [...value.value] : value.value),
    (newVal) => {
      if (newVal && !props.multiple && props.itemQueryRef) {
        const idToCheck = props.returnObject ? itemProps.value(newVal)?.value : newVal;
        if (idToCheck) {
          const exists = optionItems.value.some(
            (it: any) => itemProps.value(it)?.value === idToCheck
          );
          if (!exists && isRef(props.itemQueryRef)) {
            props.itemQueryRef.value = idToCheck;
          }
        }
      }

      let newIds: any[] = [];
      if (props.multiple) {
        newIds = Array.isArray(newVal)
          ? newVal.map((v: any) => (props.returnObject ? itemProps.value(v)?.value : v))
          : [];
      } else {
        if (newVal != null) {
          newIds = [props.returnObject ? itemProps.value(newVal)?.value : newVal];
        }
      }

      const keptItems = cachedSelectedItems.value.filter((item) => {
        const id = itemProps.value(item)?.value;
        return newIds.includes(id);
      });

      const currentOptions = optionItems.value;
      newIds.forEach((id) => {
        if (!keptItems.some((k) => itemProps.value(k)?.value === id)) {
          const found = currentOptions.find((opt) => itemProps.value(opt)?.value === id);
          if (found) keptItems.push(found);
        }
      });

      if (
        keptItems.length !== cachedSelectedItems.value.length ||
        keptItems.some(
          (it, idx) =>
            itemProps.value(it)?.value !== itemProps.value(cachedSelectedItems.value[idx])?.value
        )
      ) {
        cachedSelectedItems.value = keptItems;
      }
    },
    { immediate: true, deep: true }
  );

  // Initiates single-item check when field becomes enabled
  watch(
    () => props.disabled,
    (isDisabled) => {
      if (isDisabled) return;
      // A readonly field can't be opened or searched, so the option list is never shown.
      // Skip every list fetch here — a preselected value still resolves its label through
      // the single-item query (`itemQuery`), which is one request instead of a full page.
      if (props.readonly) return;

      if (singleItemCheckStatus.value === "idle" && !hasInteracted.value) {
        const shouldCheck =
          (!existingDataValue.value || (existingDataValue.value as any)[props.fieldName] == null) &&
          value.value == null;

        if (shouldCheck) {
          if (props.paginatedQuery && queryFiltersRef.value) {
            const filters = unref(queryFiltersRef);
            if (filters) {
              // Enable the query atomically with the page_size=1 change so the
              // very first HTTP request is page_size=1, not the default page_size=20.
              if (props.queryEnabled && isRef(props.queryEnabled)) {
                (props.queryEnabled as Ref<boolean>).value = true;
              }
              originalPageSize.value = filters.page_size || 20;
              filters.page_size = 1;
              singleItemCheckStatus.value = "checking";
            }
          } else if (
            props.queryAll &&
            props.queryAll.isEnabled?.value !== false &&
            !props.queryAll.data?.value &&
            !props.queryAll.isFetching.value
          ) {
            // Open the lazy gate atomically so the query auto-fetches; the forced refetch
            // below is now a no-op while disabled (skipToken).
            if (props.queryEnabled && isRef(props.queryEnabled)) {
              (props.queryEnabled as Ref<boolean>).value = true;
            }
            singleItemCheckStatus.value = "checking";
            props.queryAll.refetch();
          }
        } else {
          // Has existing value (e.g. edit mode) — enable the query so the
          // selected item's label can be resolved from the loaded list.
          if (props.queryEnabled && isRef(props.queryEnabled) && !props.queryEnabled.value) {
            (props.queryEnabled as Ref<boolean>).value = true;
          }
        }
      }
    },
    { immediate: true }
  );

  // --- Pagination ---

  const isAtBottomOfList = ref(false);

  // When a page finishes loading reset the flag so onScroll-based callers (SearchableMultiselect)
  // can trigger the next page. v-intersect resets it naturally by leaving/entering the viewport.
  watch(
    () => props.paginatedQuery?.isFetchingNextPage.value,
    (fetching) => {
      if (!fetching) isAtBottomOfList.value = false;
    }
  );

  const debouncedFetchNextPage = debounce(() => {
    if (
      props.paginatedQuery?.hasNextPage.value &&
      !props.paginatedQuery.isFetchingNextPage.value &&
      !props.paginatedQuery.isFetching.value
    ) {
      props.paginatedQuery?.fetchNextPage();
    }
  }, 150);

  const onIntersect = (isIntersecting: boolean) => {
    const wasAtBottom = isAtBottomOfList.value;
    isAtBottomOfList.value = isIntersecting;

    if (!isIntersecting) {
      debouncedFetchNextPage.cancel();
      return;
    }

    if (wasAtBottom) return;

    if (props.paginatedQuery) {
      debouncedFetchNextPage();
    }
  };

  // --- Search ---

  const debouncedEmit = debounce((val: string) => {
    if (props.paginatedQuery && queryFiltersRef.value && props.filterField) {
      const valueToEmit = props.searchTransform ? props.searchTransform(val) : val;
      (queryFiltersRef.value as any)[props.filterField] = valueToEmit;
    }
  }, 300);

  function onSearchUpdate(val: string) {
    isSearching.value = !!val;
    debouncedEmit(val);
    onSearchChange?.(val);
  }

  onBeforeUnmount(() => {
    debouncedEmit.cancel();
    debouncedFetchNextPage.cancel();
  });

  // --- Interaction ---

  function handleInteraction() {
    if (!hasInteracted.value) {
      hasInteracted.value = true;
      // `enabled` is authoritative (skipToken), so a forced refetch can no longer load a
      // disabled lazy query. Opening the gate here mirrors `openMobileDialog` and lets the
      // query auto-fetch on interaction.
      if (props.queryEnabled && isRef(props.queryEnabled)) {
        (props.queryEnabled as Ref<boolean>).value = true;
      }
      if (
        props.paginatedQuery &&
        !props.paginatedQuery.data?.value?.pages?.length &&
        !props.paginatedQuery.isFetching.value
      ) {
        props.paginatedQuery.refetch();
      }
      if (
        props.queryAll &&
        props.queryAll.isEnabled?.value !== false &&
        !props.queryAll.data?.value
      )
        props.queryAll.refetch();
    }
  }

  // --- Filtering (for desktop v-autocomplete) ---

  const filterKeys = computed(() => props.filterKeys);

  const customFilter = (itemTitle: string, queryText: string, item: any) => {
    if (effectiveNoFilters.value) return true;
    const keys = Array.isArray(filterKeys.value) ? filterKeys.value : [filterKeys.value];
    if (!keys?.length || !keys[0]) {
      const text = itemTitle.toLowerCase();
      const query = queryText.toLowerCase();
      return text.indexOf(query) > -1;
    }

    const query = queryText.toLowerCase();

    return keys.some((key) => {
      if (!key) return false;

      const mappedItem = itemProps.value(item.raw);
      const propValue = mappedItem?.[key as keyof TItemProps];
      if (propValue !== undefined) {
        return String(propValue).toLowerCase().includes(query);
      }

      const rawValue = item.raw?.[key];
      return String(rawValue ?? "")
        .toLowerCase()
        .includes(query);
    });
  };

  return {
    itemProps,
    optionItems,
    cachedSelectedItems: cachedSelectedItems as Readonly<Ref<any[]>>,
    isSearching,
    hasInteracted,
    effectiveNoFilters,
    filterKeys,
    customFilter,
    handleInteraction,
    onSearchUpdate,
    onIntersect,
  };
}
